import { CSSProperties, FC, useMemo } from 'react';

import { useParams } from 'react-router-dom';

import { HugeRollingOffer } from '@appcharge/shared-ui';
import useOffersUI from 'api/useOffersUI';
import {
  Offer,
  ProgressBarPoint,
  RollingOfferProductSequence
} from 'common/contracts';
import { ESaleDiscountDisplayType } from 'constants/enums';
import Decimal from 'decimal.js';
import { ROLLING_OFFER_DEFAULTS } from 'views/OfferDesign/OfferDesign.consts';
import { normalizeBgColor } from 'views/OfferDesign/OfferDesign.utils';
import { OfferPreviewShell } from 'views/Offers/components/OfferPreviewShell';
import { resolveProgressBarPoints } from 'views/Offers/utils/resolveProgressBarPoints';

interface RollingOfferPreviewProps {
  currentOfferUI: string;
  productsSequence: RollingOfferProductSequence[];
  progressBarOffers?: Offer[];
}

const enrichProgressBarPoints = (
  rawPoints: ProgressBarPoint[] | undefined,
  progressBarOffers: Offer[]
) => {
  if (!rawPoints || rawPoints.length === 0) return undefined;
  const first = rawPoints[0];
  return resolveProgressBarPoints({
    progressBarId: first.barId ?? null,
    points: first.points ?? null,
    progressBarOffers
  });
};

const ROLLING_CONTAINER_STYLE: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  flexDirection: 'column',
  overflow: 'hidden'
};

const mapProductsSequence = (
  productsSequence: RollingOfferProductSequence[],
  progressBarOffers: Offer[]
) => {
  return productsSequence.map((item) => ({
    ...item,
    prices: {
      priceInUsdCents: new Decimal(item?.priceInUsdCents || 0)
        .mul(100)
        .toNumber(),
      priceDisplay: `$${item?.priceInUsdCents || 0}`,
      currencyCode: 'USD'
    },
    products: item?.products.map((productItem) => ({
      ...productItem,
      ...productItem?.product,
      images: {
        product:
          productItem?.product?.images?.find((img) => img.type === 'product')
            ?.url ||
          productItem?.product?.images?.[0]?.url ||
          null
      }
    })),
    salePercentage: item?.productSale?.sale || 0,
    salePercentageDisplayType: ESaleDiscountDisplayType.PERCENTAGE,
    badges: item?.badges || [],
    progressBarPoints: enrichProgressBarPoints(
      item?.progressBarPoints,
      progressBarOffers
    )
  }));
};

export const RollingOfferPreview: FC<RollingOfferPreviewProps> = ({
  currentOfferUI,
  productsSequence,
  progressBarOffers
}) => {
  const { publisherId } = useParams();
  const { getOfferUI } = useOffersUI(publisherId, currentOfferUI);

  const offerUIData = getOfferUI?.data;

  const subRollingOffersProducts = useMemo(
    () =>
      productsSequence
        ? mapProductsSequence(productsSequence, progressBarOffers ?? [])
        : [],
    [productsSequence, progressBarOffers]
  );

  return (
    <OfferPreviewShell containerStyle={ROLLING_CONTAINER_STYLE}>
      {({ storeScreen }) => {
        if (!offerUIData) return null;

        const rollingOffer = offerUIData.rollingOffer;

        return (
          <HugeRollingOffer
            isPreview
            backgroundImage={offerUIData.backgroundImage}
            horizontalBackgroundImage={offerUIData.horizontalBackgroundImage}
            headerImage={rollingOffer?.headerImage}
            backgroundOpacity={
              rollingOffer?.backgroundOpacity ??
              ROLLING_OFFER_DEFAULTS.backgroundOpacity
            }
            backgroundColor={
              normalizeBgColor(rollingOffer?.backgroundColor) ||
              ROLLING_OFFER_DEFAULTS.backgroundColor
            }
            borderColor={
              normalizeBgColor(offerUIData.borderColor) ||
              ROLLING_OFFER_DEFAULTS.borderColor
            }
            borderWidth={
              offerUIData.borderWidth ?? ROLLING_OFFER_DEFAULTS.borderWidth
            }
            indexToCollect={1}
            collectBehavior="disappear"
            arrowColor={
              rollingOffer?.arrowColor || ROLLING_OFFER_DEFAULTS.arrowColor
            }
            arrowImage={rollingOffer?.arrowImage || ''}
            subRollingOffersProducts={subRollingOffersProducts}
            subRollingOfferUiProps={{
              backgroundColor:
                normalizeBgColor(
                  rollingOffer?.subRollingOffer?.backgroundColor
                ) || ROLLING_OFFER_DEFAULTS.subOfferBackgroundColor,
              backgroundImage:
                rollingOffer?.subRollingOffer?.backgroundImage || '',
              lockImage:
                rollingOffer?.subRollingOffer?.lockImage ||
                ROLLING_OFFER_DEFAULTS.lockImage,
              buttonColor: storeScreen.buttonColor,
              collectText:
                rollingOffer?.subRollingOffer?.collectText ||
                ROLLING_OFFER_DEFAULTS.collectText,
              buttonTextColor: storeScreen.buttonTextColor
            }}
            selectOffer={async () => true}
          />
        );
      }}
    </OfferPreviewShell>
  );
};
